import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';


const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  align-self: flex-start;
  overflow-y: scroll;
  overflow-x: scroll;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

/* ----------------------------------
   Types
---------------------------------- */

export type AttachmentItem = {
  name?: string;
  fileName?: string;
  hasSpecialCharacters?: boolean;
};

type AttachmentListProps = {
  label?: string;
  attachments?: AttachmentItem[];
  handleRemove: (fileName: string) => void;
  handleDownload: (hcId: string | number | undefined, fileName: string, dispatch: unknown) => void;
  isReadonly?: boolean;
  dispatch?: unknown;
  hcId?: string | number;
  hideDownload?: boolean;
};


const AttachmentList = ({
  label,
  attachments,
  handleRemove,
  isReadonly,
  handleDownload,
  dispatch,
  hcId,
  hideDownload
}: AttachmentListProps) => {
  const fileItems =
    attachments &&
    attachments.length > 0 &&
    attachments.map((attachment) => {
      const fileName = attachment.name || attachment.fileName;

      if (!fileName) {
        return null;
      }

      return (
        <Box
          key={`i-${fileName}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Chip
            variant="outlined"
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  p: 0
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: 'var(--sprout-text-primary, #101C16)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    font: 'var(--sprout-font-body1)',
                    py: '4px',
                    paddingLeft: '8px',
                    marginRight: hideDownload ? '8px' : 0,
                    minWidth: 0,
                    maxHeight: '100vh',
                    display: 'block'
                  }}
                  title={fileName}
                >
                  {fileName}
                </Box>

                {!hideDownload && (
                  <FileDownloadOutlinedIcon
                    fontSize="small"
                    sx={{ mr: '8px' }}
                  />
                )}
              </Box>
            }
            clickable={!isReadonly && !hideDownload}
            onClick={
              !isReadonly && !hideDownload
                ? () => handleDownload(hcId, fileName, dispatch)
                : undefined
            }
            onDelete={
              !isReadonly
                ? () => handleRemove(fileName)
                : undefined
            }
            sx={{
              maxWidth: '100%',
              ...(attachment.hasSpecialCharacters
                ? {
                    color: 'var(--emphasis-ruby-red-500)',
                    borderColor: 'var(--emphasis-ruby-red-500)'
                  }
                : {}),
              '& .MuiChip-label': {
                padding: 0
              }
            }}
          />
        </Box>
      );
    });

  return (
    <Wrapper data-testid="attachment-list">
      {label && (
        <Typography
          component="div"
          sx={{
            color: 'var(--Text-text-secondary, #707773)',
            font: 'var(--sprout-font-body2-bold)',
            marginBottom: 'var(--spacing-spacing-sm, 4px)'
          }}
        >
          {label}
        </Typography>
      )}

      {fileItems && fileItems.length > 0 ? (
        <ChipsContainer>{fileItems}</ChipsContainer>
      ) : null}
    </Wrapper>
  );
};

export default AttachmentList;